import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  Typography,
  Button,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
} from "@mui/material";
import ApartmentOutlinedIcon from "@mui/icons-material/ApartmentOutlined";
import DropDown from "../../Subcomponents/DropDown";
import SearchBar from "../../Subcomponents/Search";
import { useRef } from "react";
import {
  getActiveProjects,
  getWingsByProject,
  getUnitsByWings,
  getInventoryTypes,
  getOverlappingPopScopes,
} from "../../Services/Brokerage";
import { errorTost, successTost } from "../../Services/Toast";
import { toast } from "react-toastify";

const CreatePopPlanStep3 = ({ data, setData, onBack, onNext }) => {
  const [projects, setProjects] = useState([]);
  const [wings, setWings] = useState([]);
  const [units, setUnits] = useState([]);
  const [floorUnits, setFloorUnits] = useState({});
  const [inventoryTypes, setInventoryTypes] = useState({});
  const [unitsInOtherPlans, setUnitsInOtherPlans] = useState(new Set());
  const [disabledWingIds, setDisabledWingIds] = useState(new Set());
  const [loadingWings, setLoadingWings] = useState(false);
  const [disabledProjectIds, setDisabledProjectIds] = useState(new Set());
  const [projectError, setProjectError] = useState("");
  const [isResettingScope, setIsResettingScope] = useState(false);
  const [unitSearch, setUnitSearch] = useState("");
  /* -----------------------------------------
     LOAD PROJECTS
  ------------------------------------------ */
  useEffect(() => {
    const loadProjects = async () => {
      const res = await getActiveProjects();
      setProjects(res || []);
    };
    loadProjects();
  }, []);

  /* -----------------------------------------
     PROJECT SELECT WITH DATE OVERLAP LOGIC
  ------------------------------------------ */
  const handleProjectSelect = async (projectId) => {
    if (!data.startDate || !data.endDate) return;

    // 🚨 If selecting same project → do nothing
    if (projectId === data.projectId) return;

    // 🔥 HARD RESET UI FIRST
    setWings([]);
    setUnits([]);
    setFloorUnits({});
    setUnitsInOtherPlans(new Set());
    setDisabledWingIds(new Set());
    setProjectError("");

    const overlapping = await getOverlappingPopScopes(
      projectId,
      data.startDate,
      data.endDate,
    );

    const hasAnyOverlap =
      overlapping.projectBlocked ||
      overlapping.wings?.size > 0 ||
      overlapping.units?.size > 0;

    // 🚫 Entire project blocked
    if (overlapping.projectBlocked) {
      errorTost(
        "This project already has a Project-level POP plan for selected dates.",
      );
      setProjectError(
        "This project already has a Project-level POP plan for selected dates.",
      );

      setData((prev) => ({
        ...prev,
        projectId: "",
        projectName: "",
        wingIds: [],
        unitIds: [],
      }));

      return;
    }

    // 🚫 Scope = project and any overlap
    if (data.scopeType === "project" && hasAnyOverlap) {
      errorTost(
        "This project already has overlapping POP plans for selected dates.",
      );
      setProjectError(
        "This project already has a Project-level POP plan for selected dates.",
      );

      setData((prev) => ({
        ...prev,
        projectId: "",
        projectName: "",
        wingIds: [],
        unitIds: [],
      }));

      return;
    }

    // ✅ Valid project
    const selectedProject = projects.find((p) => p.projectId === projectId);

    setProjectError("");

    setData((prev) => ({
      ...prev,
      projectId,
      projectName: selectedProject?.projectName || "",
      wingIds: [],
      unitIds: [],
    }));
  };
  /* -----------------------------------------
     WING TOGGLE
  ------------------------------------------ */
  const handleWingToggle = async (wingId) => {
    if (disabledWingIds.has(wingId)) return;

    if (data.scopeType === "unit") {
      setData((prev) => ({
        ...prev,
        wingIds: [wingId],
        unitIds: [],
      }));

      const unitsData = await getUnitsByWings([wingId]);
      setUnits(unitsData || []);
    } else {
      const exists = data.wingIds?.includes(wingId);

      const updatedWingIds = exists
        ? data.wingIds.filter((w) => w !== wingId)
        : [...(data.wingIds || []), wingId];

      setData((prev) => ({
        ...prev,
        wingIds: updatedWingIds,
        unitIds: [],
      }));
    }
  };

  /* -----------------------------------------
     GROUP UNITS BY FLOOR
  ------------------------------------------ */
  useEffect(() => {
    if (!units.length) return;

    const grouped = units.reduce((acc, unit) => {
      const floor = unit.floorNo || "Unknown Floor";
      if (!acc[floor]) acc[floor] = [];
      acc[floor].push(unit);
      return acc;
    }, {});

    setFloorUnits(grouped);
  }, [units]);

  const prevScopeRef = useRef(data.scopeType);

  useEffect(() => {
    if (prevScopeRef.current !== data.scopeType) {
      // ✅ Scope actually changed
      setData((prev) => ({
        ...prev,
        projectId: "",
        projectName: "",
        wingIds: [],
        unitIds: [],
      }));

      setProjectError("");
      setWings([]);
      setUnits([]);
      setFloorUnits({});
      setUnitsInOtherPlans(new Set());
      setDisabledWingIds(new Set());
      setDisabledProjectIds(new Set());
    }

    prevScopeRef.current = data.scopeType;
  }, [data.scopeType]);

  useEffect(() => {
    if (isResettingScope) return;
    const checkProjectOverlaps = async () => {
      if (
        !data.startDate ||
        !data.endDate ||
        data.scopeType !== "project" ||
        !projects.length
      ) {
        setDisabledProjectIds(new Set());
        return;
      }

      const disabledSet = new Set();

      for (const project of projects) {
        const overlapping = await getOverlappingPopScopes(
          project.projectId,
          data.startDate,
          data.endDate,
        );

        const hasAnyOverlap =
          overlapping.projectBlocked ||
          overlapping.wings?.size > 0 ||
          overlapping.units?.size > 0;

        if (hasAnyOverlap) {
          disabledSet.add(project.projectId);
        }
      }

      setDisabledProjectIds(disabledSet);
    };

    checkProjectOverlaps();
  }, [projects, data.startDate, data.endDate, data.scopeType]);

  useEffect(() => {
    if (!data.projectId) return;
    if (!data.startDate || !data.endDate) return;
    if (data.scopeType !== "wing" && data.scopeType !== "unit") return;

    const recheckOverlap = async () => {
      setLoadingWings(true);

      try {
        const wingsData = await getWingsByProject(data.projectId);
        if (!wingsData?.length) {
          setLoadingWings(false);
          return;
        }

        const wingIds = wingsData.map((w) => w.wingId);
        const allUnits = await getUnitsByWings(wingIds);

        const overlapping = await getOverlappingPopScopes(
          data.projectId,
          data.startDate,
          data.endDate,
        );

        const disabledSet = new Set();

        // 🚫 1️⃣ If project-level plan exists → disable all wings
        if (overlapping.projectBlocked) {
          wingIds.forEach((id) => disabledSet.add(id));
        } else {
          wingsData.forEach((wing) => {
            const wingUnits = allUnits.filter((u) => u.wingId === wing.wingId);

            const wingUnitIds = wingUnits.map((u) => u.unitId);

            // 🚫 2️⃣ If wing-level plan exists → disable entire wing
            if (overlapping.wings?.has(wing.wingId)) {
              disabledSet.add(wing.wingId);
              return;
            }

            // 🔴 3️⃣ WING SCOPE LOGIC
            if (data.scopeType === "wing") {
              // If ANY unit blocked → disable wing
              const hasBlockedUnit = wingUnitIds.some((id) =>
                overlapping.units?.has(id),
              );

              if (hasBlockedUnit) {
                disabledSet.add(wing.wingId);
              }

              return;
            }

            // 🟢 4️⃣ UNIT SCOPE LOGIC
            if (data.scopeType === "unit") {
              const blockedCount = wingUnitIds.filter((id) =>
                overlapping.units?.has(id),
              ).length;

              const totalCount = wingUnitIds.length;

              // Disable wing ONLY if ALL units blocked
              if (totalCount > 0 && blockedCount === totalCount) {
                disabledSet.add(wing.wingId);
              }
            }
          });
        }

        setUnitsInOtherPlans(overlapping.units || new Set());
        setDisabledWingIds(disabledSet);
        setWings(wingsData);
      } catch (err) {
        console.error("Overlap check failed:", err);
      }

      setLoadingWings(false);
    };

    recheckOverlap();
  }, [data.projectId, data.startDate, data.endDate, data.scopeType]);
  /* -----------------------------------------
     FETCH INVENTORY TYPES
  ------------------------------------------ */
  useEffect(() => {
    const fetchInventoryTypes = async () => {
      const ids = [...new Set(units.map((u) => u.inventoryTypeId))];
      if (!ids.length) return;

      const types = await getInventoryTypes(ids);

      const map = types.reduce((acc, t) => {
        acc[t.inventoryTypeId] = t.type;
        return acc;
      }, {});

      setInventoryTypes(map);
    };

    fetchInventoryTypes();
  }, [units]);

  /* -----------------------------------------
     UNIT TOGGLE
  ------------------------------------------ */
  const handleUnitToggle = (unitId) => {
    if (unitsInOtherPlans.has(unitId)) return;

    setData((prev) => ({
      ...prev,
      unitIds: prev.unitIds?.includes(unitId)
        ? prev.unitIds.filter((id) => id !== unitId)
        : [...(prev.unitIds || []), unitId],
    }));
  };

  /* -----------------------------------------
   FILTERED UNITS (SEARCH)
------------------------------------------ */

  const getFilteredUnits = (unitsList) => {
    if (!unitSearch.trim()) return unitsList;

    const search = unitSearch.toLowerCase();

    return unitsList.filter((unit) => {
      const unitNoMatch = unit.unitNo?.toLowerCase().includes(search);

      const typeName =
        inventoryTypes[unit.inventoryTypeId]?.toLowerCase() || "";

      const typeMatch = typeName.includes(search);

      return unitNoMatch || typeMatch;
    });
  };

  /* -----------------------------------------
   SELECT ALL UNITS
------------------------------------------ */

  const handleSelectAllUnits = () => {
    const allAvailableUnitIds = units
      .filter((u) => !unitsInOtherPlans.has(u.unitId))
      .map((u) => u.unitId);

    setData((prev) => ({
      ...prev,
      unitIds: allAvailableUnitIds,
    }));
  };

  /* -----------------------------------------
   SELECT ALL BY TYPE
------------------------------------------ */

  const handleSelectByType = (typeId) => {
    const typeUnitIds = units
      .filter(
        (u) => u.inventoryTypeId === typeId && !unitsInOtherPlans.has(u.unitId),
      )
      .map((u) => u.unitId);

    setData((prev) => ({
      ...prev,
      unitIds: [...new Set([...(prev.unitIds || []), ...typeUnitIds])],
    }));
  };

  /* -----------------------------------------
     UI
  ------------------------------------------ */

  const steps = [
    "Plan Details",
    "POP Validity",
    "Scope Type",
    "Scope Selection",

    "Calculation Rules",
    "Eligibility",
    "Conflict Settings",
    "CP Sign-Off",
    "Review",
  ];

  return (
    <Box sx={{ p: 1}}>
      <Stepper activeStep={3} alternativeLabel sx={{ mb: 3 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel
              StepIconProps={{
                sx: {
                  width: 36,
                  height: 36,
                  fontSize: "1rem",
                  "&.Mui-completed": { color: "#2563EB" },
                  "&.Mui-active": {
                    color: "#2563EB",
                    transform: "scale(1.15)",
                  },
                },
              }}
              sx={{
                "& .MuiStepLabel-label": {
                  fontSize: "0.85rem",
                  mt: 1,
                },
              }}
            >
              {label}
            </StepLabel>
          </Step>
        ))}
      </Stepper>

      <Typography sx={{ mb: 2 }}>
        Step 3 of 9: <b>Scope Selection</b>
      </Typography>

      <Box sx={{ maxWidth: 320, mb: 2 }}>
        <DropDown
          label="Project"
          selectedValue={data.projectId || ""}
          onChangeSelect={handleProjectSelect}
          list={projects.map((p) => ({
            label: p.projectName,
            value: p.projectId,
          }))}
          placeholder="Select Project"
          isSearch
        />

        {projectError && (
          <Typography
            variant="caption"
            sx={{ color: "error.main", mt: 1, display: "block" }}
          >
            {projectError}
          </Typography>
        )}
      </Box>

      {(data.scopeType === "wing" || data.scopeType === "unit") && (
        <>
          {loadingWings ? (
            <CircularProgress />
          ) : (
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              {wings.map((wing) => {
                const selected = data.wingIds?.includes(wing.wingId);
                const isDisabled = disabledWingIds.has(wing.wingId);

                return (
                  <Card
                    key={wing.wingId}
                    onClick={() => !isDisabled && handleWingToggle(wing.wingId)}
                    sx={{
                      cursor: isDisabled ? "not-allowed" : "pointer",
                      opacity: isDisabled ? 0.5 : 1,
                      border: selected
                        ? "2px solid #4F6BED"
                        : "1px solid #D0D5DD",
                      borderRadius: "8px",
                      padding: "16px",
                      width: "150px",
                      textAlign: "center",
                    }}
                  >
                    <ApartmentOutlinedIcon
                      sx={{ fontSize: "32px", color: "#4F6BED" }}
                    />
                    <Typography>{wing.wingName}</Typography>
                  </Card>
                );
              })}
            </Box>
          )}
        </>
      )}

      {data.scopeType === "unit" && data.wingIds?.length > 0 && (
        <>
          {/* 🔍 Search + Global Controls */}
          <Box
            sx={{
              display: "flex",
              gap: 2,
              mb: 2,
              mt: 5,
              flexWrap: "wrap",
              alignItems: "center",
            }}
          >
            <SearchBar
              placeholder="Search by Unit No or Type..."
              width="280px"
              id="unit-search"
              value={unitSearch}
              search={(e) => setUnitSearch(e.target.value)}
              style={{
                padding: "8px",
                width: "250px",
                borderRadius: "6px",
                border: "1px solid #D0D5DD",
              }}
            />

            <Button variant="outlined" onClick={handleSelectAllUnits}>
              Select All (Available)
            </Button>

            <Typography>Selected: {data.unitIds?.length || 0}</Typography>
          </Box>

          {/* 🏷 GLOBAL TYPE-WISE SELECTION */}
          <Box sx={{ display: "flex", gap: 1, mb: 3, flexWrap: "wrap" }}>
            {[...new Set(units.map((u) => u.inventoryTypeId))].map((typeId) => (
              <Button
                key={typeId}
                size="small"
                variant="outlined"
                onClick={() => handleSelectByType(typeId)}
              >
                Select All {inventoryTypes[typeId]}
              </Button>
            ))}
          </Box>

          {/* 📦 Units By Floor */}
          {Object.keys(floorUnits).map((floor) => {
            const filteredUnits = getFilteredUnits(floorUnits[floor]);
            if (!filteredUnits.length) return null;

            return (
              <Box key={floor} sx={{ mb: 3 }}>
                <Typography variant="h6">{floor}</Typography>

                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                  {filteredUnits.map((unit) => {
                    const selected = data.unitIds?.includes(unit.unitId);
                    const isDisabled = unitsInOtherPlans.has(unit.unitId);

                    return (
                      <Card
                        key={unit.unitId}
                        onClick={() =>
                          !isDisabled && handleUnitToggle(unit.unitId)
                        }
                        sx={{
                          cursor: isDisabled ? "not-allowed" : "pointer",
                          opacity: isDisabled ? 0.5 : 1,
                          border: selected
                            ? "2px solid #4F6BED"
                            : "1px solid #D0D5DD",
                          borderRadius: "8px",
                          padding: "16px",
                          width: "150px",
                          textAlign: "center",
                        }}
                      >
                        <ApartmentOutlinedIcon
                          sx={{ fontSize: "28px", color: "#4F6BED" }}
                        />
                        <Typography>{unit.unitNo}</Typography>
                        <Typography variant="caption">
                          {inventoryTypes[unit.inventoryTypeId]}
                        </Typography>
                      </Card>
                    );
                  })}
                </Box>
              </Box>
            );
          })}
        </>
      )}

      <Box sx={{ display: "flex", justifyContent: "space-between", mt: 3 }}>
        <Button variant="outlined" onClick={onBack}>
          ← Back
        </Button>

        <Button
          variant="contained"
          onClick={() => {
            if (!data.projectId) {
              errorTost("Please select a project.");
              return;
            }

            if (
              data.scopeType === "wing" &&
              (!data.wingIds || data.wingIds.length === 0)
            ) {
              errorTost("Please select at least one wing.");
              return;
            }

            if (
              data.scopeType === "unit" &&
              (!data.unitIds || data.unitIds.length === 0)
            ) {
              errorTost("Please select at least one unit.");
              return;
            }

            onNext();
          }}
        >
          Next →
        </Button>
      </Box>
    </Box>
  );
};

export default CreatePopPlanStep3;
